import { FC } from "react";
import { Select, MenuItem, ListItemText, FormControl, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { scheduleRoleDa } from "mocks/roleDA";
import {
  daStatusConstants,
  fleetReportInvoiceFilter,
  scheduleRoleListTemplate,
  scheduleStaffRoleListTemplate,
  userRoles,
} from "constants/constants";

export type Station = {
  id: number;
  title: string;
};

export interface SelectFieldOption {
  value: number | string;
  label: string;
}

interface SelectFieldProps {
  options: SelectFieldOption[];
  value: any;
  onChange: any;
  label?: string;
  allLabel?: string;
  hasAllOption?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  hideLabel?: boolean;
}

interface SelectProps {
  value: string | number | any;
  onChange: any;
  disabled?: boolean;
  limitedTo?: string[];
  hasAllOption?: boolean;
  hideLabel?: boolean;
  fullWidth?: boolean;
  scheduleUserRole?: number;
}

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 210,
      width: 200,
    },
  },
  variant: "menu" as const,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom" as const, horizontal: "center" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "center" as const },
};

export const SelectField: FC<SelectFieldProps> = ({
  options,
  value,
  onChange,
  label,
  allLabel,
  hasAllOption,
  disabled,
  fullWidth,
  hideLabel,
}) => {
  const classes = useStyles();
  const allText = allLabel || (label ? `All ${label}` : "All");

  return (
    <FormControl className={classes.root}>
      {!hideLabel && label && <Typography className={classes.label}>{label}</Typography>}
      <Select
        className={`${classes.select} ${fullWidth ? classes.fullWidth : ""}`}
        variant="outlined"
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
        MenuProps={MENU_PROPS}
        disabled={disabled}
      >
        {hasAllOption && (
          <MenuItem value={0}>
            <ListItemText primary={allText} />
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const StationSelect: FC<SelectProps> = ({ limitedTo, hideLabel, ...props }) => {
  const { userData } = useTypeSelectors((state) => state.auth);
  const options = userData.station_spots
    .filter((item) => (limitedTo ? limitedTo.includes(item.station.tag) : true))
    .sort((a, b) => a.station.id - b.station.id)
    .map((spot) => ({
      value: spot.station.id,
      label: spot.station.title,
    }));

  return (
    <SelectField
      label="Station"
      allLabel="All Stations"
      options={options}
      hideLabel={hideLabel}
      {...props}
    />
  );
};

export const ScheduleRoleSelect: FC<SelectProps> = ({ limitedTo, scheduleUserRole, ...props }) => {
  const template = scheduleUserRole === 5 ? scheduleRoleListTemplate : scheduleStaffRoleListTemplate
  const options = scheduleRoleDa
    .sort(
      (a, b) =>
        template.indexOf(a.roleScheduleId) -
        template.indexOf(b.roleScheduleId),
    )
    .map((role) => ({
      value: role.roleScheduleId,
      label: role.name,
    }));

  return <SelectField label="Role" options={options} {...props} />;
};

export const StatusSelect: FC<SelectProps> = ({ hideLabel, ...props }) => {
  const options = daStatusConstants
    .filter((status) => [1, 5].includes(status.value))
    .map((status) => ({
      value: status.value,
      label: status.label,
    }));

  return (
    <SelectField
      label="Status"
      allLabel="All Status"
      options={options}
      hasAllOption
      hideLabel={hideLabel}
      {...props}
    />
  );
};

export const RoleSelect: FC<SelectProps> = ({ hideLabel, ...props }) => {
  const options = userRoles
    .filter((role) => role.value !== 0)
    .map((role) => ({
      value: role.value,
      label: role.name,
    }));

  return (
    <SelectField
      label="Role"
      allLabel="All Roles"
      options={options}
      hasAllOption
      hideLabel={hideLabel}
      {...props}
    />
  );
};

export const InvoiceStatusSelect: FC<SelectProps> = ({ hideLabel, ...props }) => {
  const options = fleetReportInvoiceFilter
    .map((option) => ({
      value: option.value,
      label: option.name,
    }));

  return (
    <SelectField
      label="Invoice Status"
      options={options}
      hideLabel={hideLabel}
      {...props}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "block",
  },
  label: {
    color: "#5F6164",
    fontSize: 14,
    marginBottom: 4,
    textTransform: "capitalize",
  },
  select: {
    minWidth: 150,
    width: 150,
  },
  fullWidth: {
    width: "100%",
  },
  "@media (max-width: 1280px)": {
    root: {
      flex: 1,
      minWidth: 0,
    },
    select: {
      width: "100%",
      minWidth: 0,
    },
  },
}));
